import { Pass, Seq, Choice, Cont, Dep, ChangeForm,  setObjectValue } from "../../framework/Processes";
import { Application, External } from "../../framework/comp/Lambda/Constructors";
import { FunctionType } from "../../framework/comp/Types";
import MultivariateFunction from "../../framework/comp/EInstances/MultivariateFunction";
import MenuProcess from "./MenuProcess";
import SubTaskProcess from "./SubTaskProcess";
import AdviceProcess from "./AdviseSaveProc";
import { currentScore, maxAvailableScore, calcScore, calcMaxScore} from "../score";
import { getNonCompTasks } from "../utility/getNonCompTasks";
import recoveryFromHistory from "../recoveryFromHistory";
import { resetEmotionState } from "../../emotionModel";
import React from "react";

/**
 * Module-interlayer between minigames objects and proccess languages 
 * 
 * @module AtomicMinigameProcesses
 */

const technicalGameTypes = ["Intro", "Greeting", "Final", "End"];

/**
 * Convert a minigame to a process.
 * @param {Minigame} minigame - a minigame
 * @param {Process} [nextProcess] - continuation (next process to run)
 * @param {Object} config - current game config
 * @return {Process} a process that corresponds to the minigame attached to a subject "Player"
 */
function AtomicMinigameProcess(minigame, nextProcess = Pass, config = {type: "stub"}) {
	return Dep(External(function(state) {
		const history = state.objects.history.filter(hr => hr.minigame);
		const nextAttemptMsg = "Сейчас вам будет повторно предложена задача, на которую вы ранее дали неправильный ответ.";
		const firstAttempt = minigame.silent || !history.filter(({ minigame: { name }, finished, type }) => type && name === minigame.name && !finished).length;
		const changeFormProc = Dep(External(function(state) {
			const history = state.objects.history.filter(hr => hr.type);
			const revHistory = history.slice().reverse();
			const roundCount = history.filter(elem => elem.minigame.name === "final").length + 1;
			const isGameInProgress = state.objects.init && !state.objects.endofGame;
			const isRoundInProgress = state.objects.init && !(technicalGameTypes.includes(minigame.minigameType));
			const form = {
				type: "GameFrame",
				emotionState: resetEmotionState(state.objects.emotionState),
				score: { 
					current: currentScore(state.objects.history), 
					max: maxAvailableScore(state.objects.history),
					old: state.objects.history.length > 1 ? currentScore(state.objects.history.slice(0, state.objects.history.length-1)) : 0
				},
				title: minigame.title,
				messages: minigame.messages && 
					minigame.messages(minigame, state.objects.help),
				subform: minigame.form,
				roundCount: roundCount,
				roundQstCount: { current: calcCurrentQstCount(revHistory, state.objects.curcount), max: state.objects.curcount},
				attemps: {
					current: history.filter(({ minigame: { name }}) => name === minigame.name).length + 1,
					max: GiveDefaultMaxAttemp(state) + 1
				},
				markers: {
					subtask: minigame.name.indexOf("-subqst") !== -1,
					subtaskf: calcSubTaskMarker(history, minigame.name),
					advices: state.objects.history.filter(hr => !hr.type && hr.minigame.name === minigame.name).map(hr => hr.adviseId)
				},
				statuses: {
					isGameInProgress: isGameInProgress,
					isRoundInProgress: isRoundInProgress,
					isGameEnded: state.objects.endofGame
				},
				isCompleted: minigame.isCompleted,
				showTime: false
			};
			const continuation = Application(Application(
				MultivariateFunction(MinigameContinuation, 3, ["Process", "Object", "Object"], "Process"),
				External(Seq([minigame.postprocess || Pass, nextProcess]), "Process")),
				External(minigame, "Object"));
			const minigameProc = Cont("Player", form, continuation);
			let menuHistoryList = history.filter(elem => !technicalGameTypes.includes(elem.minigame.minigameType));
			let menuMinigameList = state.objects.menu.map(mg => setMinigameStatus(mg, history, state.objects.currentMinigame, config));
				// if(config.type === "menu") {menuMinigameList = menuMinigameList.filter(mg => mg.name !== state.objects.currentMinigame)};		
			const subtaskReturnProc = AtomicMinigameProcess(minigame, nextProcess, config);
			const subtaskProc = SubTaskProcess({...form, name: minigame.name}, AtomicMinigameProcess(minigame, nextProcess, config));
			let menuObject = {fullList: menuMinigameList, historyList: menuHistoryList};
			const menuReturnProc = Seq([setObjectValue("prcType", "main"), AtomicMinigameProcess(minigame, nextProcess, config, "return")]);
			
			const adviseProc = AdviceProcess({...form, name: minigame.name}, AtomicMinigameProcess(minigame, nextProcess, config), state);
			const menuProc = MenuProcess(menuObject, state.objects.prcType === "menu" ? Pass : menuReturnProc);
			return ChangeForm(Choice([minigameProc, menuProc, subtaskProc, adviseProc]), menuFormMap, menuMoveMap);
		}, "Function", FunctionType("Object", "Process")));

		if (firstAttempt) {
			return changeFormProc;
		}
		else {
			return Seq([
				ShowMessage(nextAttemptMsg, minigame, config),
				changeFormProc
			]);
		}
		
	}, "Function", FunctionType("Object", "Process")));
}

const menuFormMap = `
	(function (aform) {
	  if (aform.type !== 'SumForm') return aform;
	  var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();
	  var _aform$subforms = _slicedToArray(aform.subforms, 2),
	      form = _aform$subforms[0],
	      menu = _aform$subforms[1].menu;
		  subtask = _aform$subforms[2];
		  advise = _aform$subforms[3];
		  

	  return Object.assign({}, form, { menu: menu }, subtask, advise);
	})
`
//`(aform) => { if (aform.type !== 'SumForm') return aform; 
//const { subforms: [ form, { menu } ] } = aform; return { ...form, menu } }`;
//

const menuMoveMap = `
	(function () {
	  return function (variant) {
	    return variant;
	  }.toString();
	})
`
//`() => ((variant) => variant).toString()`;

const attempts = (name, history) => history.filter(({ minigame }) => minigame.name === name).length;

/**
 * Process of adding a history record.
 * @param {Minigame} minigame - played minigame
 * @param {*} res - result of played minigame (e.g. move of atomic minigame)
 * @return {Process} a process that adds a history record to `state.objects.history`
 */
function HistoryRecord(minigame, res) {
	return Dep(External(function (state) {
		var historyRecord = {
			minigame,
			playerAns: res,
			type: minigame.name.indexOf("-subqst") !== -1 ? "subtask" : "task",
			score: calcScore(minigame, res),
			maxScore: calcMaxScore(minigame, res),
			completed: !minigame.isCompleted || minigame.isCompleted(minigame.form, res),
			finished: !minigame.isCompleted || minigame.isCompleted(minigame.form, res) || (minigame.maxTry === 1)
		};
		const maxAttempts = minigame.maxTry || GiveDefaultMaxAttemp(state);
		const terminate = (!historyRecord.completed && attempts(minigame.name, state.objects.history) >= maxAttempts);
		//const msg = `Превышено допустимое количество неправильных ответов! Я рекомендую тебе ещё раз повторить эту тему перед повторным прохождением игры. Обязательно проверь свои знания по теме '${minigame.title}'.`;
		const mgMsgObject = minigame.messages && minigame.messages(minigame, state.objects.help);
		const onFinishMsg = mgMsgObject ? mgMsgObject.onFinish && mgMsgObject.onFinish(res) : false;
		let onFinishMsgProc = onFinishMsg ? ShowMessage(onFinishMsg, minigame) : Pass;
		const failMsg = <div><p>Лимит попыток ответа на вопрос исчерпан!</p><p>Перед повторным прохождением игры рекомендуется повторить тему «{minigame.title}».</p></div>;
		let failMsgProc = Pass;
		if (terminate && res !== -1) {
			historyRecord.completed = false;
			historyRecord.finished = true;
			failMsgProc = ShowMessage(failMsg, minigame);
		}
		return Seq([minigame.silent ? Pass : setObjectValue("history", historyRecord, [state.objects.history.length]), onFinishMsgProc, failMsgProc]);
	}, "Function", FunctionType("Object", "Process")));
}

/**
 * Methods for embedding process of history update 
 * 
 * @param {Process} [nextProcess] - continuation (next process to run)
 * @param {Minigame} minigame - played minigame
 * @param {*} res - player answer to current minigame
 * @returns {Process} - Seq-process, containing process of history update and continuation
 */
function MinigameContinuation(nextProcess, minigame, res) {
	if(res === -1){
		return nextProcess;
	}
	return Seq([
		HistoryRecord(minigame, res),
		nextProcess,
	]);
}

function calcAdviseMarkers(history, name){
	if(history.findIndex(hr => hr.name === name)){

	}
}

function calcSubTaskMarker(history, mgName){
	return history.findIndex(({ minigame: { name }}) => name === mgName + "-subqst") !== -1;
}

/**
 * Create additional process for showing atomic non-stylized messages to user
 * 
 * @param {string} msg - string
 * @param {*} minigame - current minigame
 * @param {*} res - current game config
 * @returns {Process} - Autofinish process
 */
export function ShowMessage(msg, minigame, config) {
	return AtomicMinigameProcess({
		...minigame,
		form: { type: "AutoFinish" },
		isCompleted: () => true,
		messages: () => ({ onStart: msg }),
		silent: true,
	}, Pass, config, "showmessage");
}

export function GiveDefaultMaxAttemp(state){
	if(state.objects.difficulty === 0) return 3;
	else return 2;
}

function calcCurrentQstCount(history, max){
	let result = 0;
	let res = history.findIndex(elem => elem.minigame.name === "count-rest");
	let chistory = history.filter(elem => elem.minigame.theme !== "subqst" && !technicalGameTypes.includes(elem.minigame.minigameType ));
	result = res === -1 ? chistory.length + 1 : chistory.filter((elem, index) => index < res).length + 1;
	result = result === 0 ? 1 : result;
	return result > max ? max : result;
}

function setMinigameStatus(minigame, history, currentMinigameName, config){
	minigame.status = "";
	let lastHistoryRecord = history.filter(hr => hr.minigame.name === minigame.name).pop();
	let isAsked = lastHistoryRecord !== undefined;
	let isFinished = isAsked && lastHistoryRecord.finished;
	if(!isFinished){
		if(isAsked){
			minigame.status = "asked";
		}
		if(config.type === "menu"){
			if(minigame.name === config.minigames[0].name){
				minigame.status = "current";
			}
		}
		else if(minigame.name === currentMinigameName){
			minigame.status = "current";
		}
	}
	else {
		minigame.status = lastHistoryRecord.completed ? "finished_good" : "finished_bad";
	}
	if(minigame.components){
		minigame.components.sequence.map(mg => setMinigameStatus(mg, history, currentMinigameName, config))
	}
	return minigame;
}

export default AtomicMinigameProcess;